"use client";

import dayjs from "dayjs";
import { useRouter } from "next/navigation";
import React, { useState, useCallback, useMemo } from "react";
import { DateInput, TextFieldSts } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import { useSubmitWithdrawalData } from "@/hooks/api/withdrawal-verification/submit-withdrawal-data";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useLoading } from "@/providers/loading-provider";
import { SecurityQuestions } from "@/types/policy-document-types";
import { trimFormValue } from "@/utils/form-input.utils";
import {
  getDobValidation,
  getDobValidationState,
} from "@/utils/policy-document.utils";
import { isValidDate } from "@/utils/travel-date.utils";

import WithdrawalModal from "./withdrawal-modal";
import Styles from "../policy-document-section/policy-content/form-content.module.css";

interface WithdrawalFormContentProps {
  verificationQuestionTitle: string;
  postcodeLabel?: string;
  postcodeInvalidErrorMessage?: string;
  dobLabel: string;
  dobMaxAge: string;
  dobMinAge: string;
  dobInvalidAge: string;
  verifyButtonLabel: string;
  modalTriggerButtonLabel?: string;
  verificationModalTitle: string;
  verificationModalContent: string;
  unauthorizedModalContent: string;
  modalCloseButtonLabel: string;
  withdrawalId: string | null;
  showPostCode?: boolean;
  showPhoneNumber?: boolean;
  postCodeMinLength?: number;
  postCodeMaxLength?: number;
  postCodeRegex?: string;
  phoneNumberLength?: number;
  phoneNumberRegex?: string;
}

function WithdrawalFormContent({
  verificationQuestionTitle,
  postcodeLabel = "Postcode",
  postcodeInvalidErrorMessage = "Please enter a valid postcode",
  dobLabel,
  dobMaxAge,
  dobMinAge,
  dobInvalidAge,
  verifyButtonLabel,
  verificationModalTitle,
  verificationModalContent,
  unauthorizedModalContent,
  modalCloseButtonLabel,
  withdrawalId,
  showPostCode = true,
  showPhoneNumber = false,
  postCodeMinLength = 4,
  postCodeMaxLength = 8,
  postCodeRegex = String.raw`^(\d{5}|\d{4}|\d{4}[- ]?\d{3})$`,
  phoneNumberLength = 5,
  phoneNumberRegex = "^[0-9]{5}$",
}: Readonly<WithdrawalFormContentProps>) {
  const isDesktop = useMediaQuery("(min-width: 600px)");
  const router = useRouter();
  const { showLoading, hideLoading } = useLoading();

  const [formData, setFormData] = useState({
    postCode: "",
    phoneNumber: "",
    dateValue: "",
    age: "",
    invalidDate: "",
  });
  const [postCodeError, setPostCodeError] = useState<string>("");
  const [phoneNumberError, setPhoneNumberError] = useState<string>("");
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [unauthorizedModalOpen, setUnauthorizedModalOpen] = useState(false);

  const submitWithdrawalData = useSubmitWithdrawalData(withdrawalId);

  const handleInputChange = useCallback((date: Date) => {
    if (isValidDate(date) && date) {
      const currentDate = dayjs();
      const birthDate = dayjs(date);
      const currentAge = dayjs().diff(birthDate, "year");
      const formattedDate = dayjs(date, "DD/MM/YYYY").toString();

      // Check if the birth date is in the future
      const isFutureDate = birthDate.isAfter(currentDate);
      let ageValue = 0;
      if (isFutureDate) {
        if (currentAge === 0) {
          ageValue = -0.5;
        } else if (currentAge < 0) {
          ageValue = currentAge;
        }
      } else {
        ageValue = currentAge;
      }
      setFormData((prev) => ({
        ...prev,
        dateValue: formattedDate,
        age: ageValue.toString(),
        invalidDate: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        dateValue: "",
        age: "",
        invalidDate: "error",
      }));
    }
  }, []);

  const handlePostCodeChange = useCallback(
    (value: string) => {
      const regex = new RegExp(postCodeRegex);
      const trimmedValue = value.replace(/\s+/g, " ").trim();
      const isInvalidPostcode =
        trimmedValue?.length > 0 && !regex.test(trimmedValue);

      setPostCodeError(isInvalidPostcode ? postcodeInvalidErrorMessage : "");
      setFormData((prev) => ({ ...prev, postCode: trimmedValue }));
    },
    [postcodeInvalidErrorMessage, postCodeRegex]
  );

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      const regex = new RegExp(phoneNumberRegex);
      const isInvalid = value?.length > 0 && !regex.test(value);

      setPhoneNumberError(isInvalid ? "Invalid telephone number" : "");
      setFormData((prev) => ({ ...prev, phoneNumber: value }));
    },
    [phoneNumberRegex]
  );

  const handleSubmit = useCallback(async () => {
    showLoading();

    const formattedDate = formData.dateValue
      ? dayjs(formData.dateValue).format("DD/MM/YYYY")
      : "";

    const payloadBase: Partial<SecurityQuestions> = {
      "2": formattedDate,
    };

    // Trim before the guards so whitespace-only values don't pass the truthy check
    const phoneNumber = trimFormValue(formData.phoneNumber);

    // Add fields based on what's enabled
    if (showPostCode && formData.postCode) {
      payloadBase["1"] = formData.postCode;
    }

    // bff doesn't accept both the postcode and phone number, so we have to pass the phone number to the same slot as the postcode
    if (showPhoneNumber && phoneNumber) {
      payloadBase["1"] = phoneNumber;
    }

    const response = await submitWithdrawalData(
      payloadBase as SecurityQuestions
    );

    hideLoading();

    if (response.success) {
      // Navigate to withdrawal success page
      router.push("/withdrawal");
      return;
    }

    // Handle too many attempts (code 1062) - no remainingAttempts in response
    if (response.isTooManyAttempts) {
      setUnauthorizedModalOpen(true);
      return;
    }

    // Handle security question validation failure (code 1063)
    if (
      response.isValidationFailed &&
      response.remainingAttempts !== undefined &&
      response.remainingAttempts < 3
    ) {
      setUnauthorizedModalOpen(true);
      return;
    }

    // Handle HTTP 401 Unauthorized
    if (response.statusCode === 401) {
      setVerificationModalOpen(true);
      return;
    }

    // Default: show verification modal for all other error cases
    setUnauthorizedModalOpen(true);
  }, [
    formData,
    showPostCode,
    showPhoneNumber,
    submitWithdrawalData,
    router,
    showLoading,
    hideLoading,
  ]);

  const isFormInvalid = useMemo(() => {
    let baseValidation =
      !formData.dateValue ||
      getDobValidationState(formData.dateValue, formData.age);

    // Add postcode validation if enabled
    if (showPostCode) {
      baseValidation = baseValidation || !formData.postCode || !!postCodeError;
    }

    // Add phone number validation if enabled
    if (showPhoneNumber) {
      baseValidation =
        baseValidation || !formData.phoneNumber || !!phoneNumberError;
    }

    return baseValidation;
  }, [
    formData,
    postCodeError,
    phoneNumberError,
    showPostCode,
    showPhoneNumber,
  ]);

  return (
    <div
      data-testid="withdrawal-formContent-testid"
      className={`${Styles.form_content} flex flex-col gap-spacing-m md:gap-spacing-l`}
    >
      {verificationModalOpen && (
        <WithdrawalModal
          isOpen={verificationModalOpen}
          setIsOpen={setVerificationModalOpen}
          content={{
            modalTitle: verificationModalTitle,
            modalContent: verificationModalContent,
            modalCloseButtonLabel,
          }}
        />
      )}

      {unauthorizedModalOpen && (
        <WithdrawalModal
          isOpen={unauthorizedModalOpen}
          setIsOpen={setUnauthorizedModalOpen}
          content={{
            modalTitle: verificationModalTitle,
            modalContent: unauthorizedModalContent,
            modalCloseButtonLabel,
          }}
        />
      )}

      <div
        data-testid="withdrawal-form-content"
        className="text-xs leading-[27px]"
      >
        {verificationQuestionTitle ??
          "Please answer the following questions to verify your identity:"}
      </div>
      <div
        className={`${Styles.postcode_block} flex flex-col gap-spacing-l w-[264px] md:w-[327px]`}
      >
        {/* postcode field */}
        {showPostCode && (
          <div className="postcode-wrapper">
            <TextFieldSts
              label={postcodeLabel ?? "Postcode"}
              name="postCode"
              id="postCode"
              width="100%"
              maxLength={postCodeMaxLength}
              minLength={postCodeMinLength}
              labelLeftPadding="16px"
              onChange={handlePostCodeChange}
              aria-label="postcode"
              state={postCodeError ? "error" : undefined}
              message={postCodeError}
              value={formData.postCode}
            />
          </div>
        )}

        {/* phone number field */}
        {showPhoneNumber && (
          <div className="phone-number-wrapper">
            <TextFieldSts
              label="Last 5 digits of telephone"
              name="phoneNumber"
              id="phoneNumber"
              width="100%"
              maxLength={phoneNumberLength}
              minLength={phoneNumberLength}
              labelLeftPadding="16px"
              onChange={handlePhoneNumberChange}
              aria-label="phone number"
              state={phoneNumberError ? "error" : undefined}
              message={phoneNumberError}
              value={formData.phoneNumber}
            />
          </div>
        )}

        <div className={`${Styles.date_picker_wrapper} flex flex-col relative`}>
          <DateInput
            isDOBScrollable={!isDesktop}
            defaultDate={
              formData.dateValue &&
              isValidDate(formData.dateValue as unknown as Date)
                ? new Date(formData.dateValue)
                : undefined
            }
            hintLabel={dobLabel ?? "DOB"}
            brand="staysure"
            dobErrorMsgClass={Styles.errorOfDob}
            dobWrapperClass={`${Styles.dobWrapper}`}
            isError={getDobValidationState(formData.dateValue, formData.age)}
            customizedMessage={
              getDobValidation(
                formData.age,
                formData.dateValue,
                dobMinAge,
                dobInvalidAge,
                dobMaxAge
              ) || formData.invalidDate
            }
            onChange={(e: Date) => handleInputChange(e as Date)}
          />
        </div>
      </div>
      <LabelButtonWrapper
        isDisabled={isFormInvalid}
        data-testid="submit-withdrawal-form-button"
        id="submit-withdrawal-form-button"
        label={verifyButtonLabel ?? "Verify"}
        hierarchy="primary"
        className="max-h-spacing-xxxl max-w-[111px] text-[18px] leading-[100%] font-[600] font-body-font"
        type="button"
        onClick={handleSubmit}
      />
    </div>
  );
}

export default WithdrawalFormContent;
